import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { Device } from '@twilio/voice-sdk';
import Grow from '@mui/material/Grow';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import OnCall from './components/OnCall';
import Dialer from './Dialer';
import Calling from './components/Calling';
import IncomingCall from './components/IncomingCall';
import ExternalCall from './components/ExternalCall';
import toast from 'utils/toast';
import { toggleDialer } from 'store/modules/modals/actions';
import usePusherEvent from 'hooks/usePusherEvent';
import { States } from './constants';
import { Typography } from '@mui/material';
import Spin from 'components/Spin';
import { getMe } from 'services/users';
import * as Sentry from '@sentry/browser';

const Widget = () => {
  const dispatch = useDispatch();
  const [state, setState] = useState(States.CONNECTING);
  const showDialer = useSelector((state) => state.modals.showDialer);
  const user = useSelector((s) => s.user.user);
  const [conn, setConn] = useState(null);
  const [device, setDevice] = useState(null);
  const [externalCall, setExternalCall] = useState(false); // call in progress externally
  const [connParams, setConnParams] = useState({});

  const calls_via = user?.receive_calls_via?.split(',');
  const isTwillioEnabled =
    calls_via?.includes('browser') || calls_via?.includes('both');

  const unsetupTwillioVoice = () => {
    if (device) {
      device.unregister();
      device.destroy();
      setDevice(null);
      setState(States.OFFLINE);
    }
  };

  const setupTwillioVoice = async () => {
    if (device || !isTwillioEnabled) return;
    const user = await getMe(); // get a very fresh token
    const token = user.twilio_browser_calling_token;
    console.log('Initializing Twilio Device', token);
    const device = new Device(token, {
      codecPreferences: ['opus', 'pcmu'],
      fakeLocalDTMF: true,
      enableRingingState: true,
      tokenRefreshMs: 30000,
      closeProtection: true,
      logLevel: process.env.NODE_ENV !== 'production' ? 'DEBUG' : undefined,
    });

    device.on('registered', function () {
      console.log('Twilio.Device registered!');
      setDevice(device);
      setState(States.READY);
    });

    device.on('error', function (error) {
      console.log('Twilio.Device Error: ' + error.message);
      Sentry.captureMessage('Twilio.Device Error: ' + error.message);
    });

    device.on('tokenWillExpire', async () => {
      console.log('Token is about to expire, refreshing it...');
      const user = await getMe();
      device.updateToken(user.twilio_browser_calling_token);
    });

    device.on('incoming', handleIncomingCall);

    device.on('unregistered', () => {
      console.log('unregistered');
      setState(States.OFFLINE);
    });

    device.register().catch((e) => {
      // console.warn(e.message);
    });
  };

  usePusherEvent(
    'user',
    'incoming_call',
    (data) => {
      console.log('incoming call', data);
      console.log('state', state);
      console.log(conn);

      try {
        const callId = data?.call_sid;

        const callHistory = sessionStorage.getItem('call_history');
        if (!callHistory) {
          sessionStorage.setItem('call_history', JSON.stringify([]));
        }
        const callHistoryParsed = JSON.parse(
          sessionStorage.getItem('call_history'),
        );
        if (callHistoryParsed.includes(callId)) {
          return; // ignore if the call is already in the history
        }

        callHistoryParsed.push(callId);
        sessionStorage.setItem(
          'call_history',
          JSON.stringify(callHistoryParsed),
        );
      } catch (e) {}
      // if the call is answered, then we need to update the state
      if (data.status === 'answered') {
        setState(States.ON_CALL);
      }

      if (state === States.READY && !conn) {
        dispatch(toggleDialer(true));
        setExternalCall(data);
      }
    },
    state,
  );

  usePusherEvent(
    'account',
    'call_ended',
    (data) => {
      if (externalCall && data.call_sid === externalCall.call_sid) {
        setExternalCall(false);
      }
    },
    externalCall,
  );

  const onCall = async (to, fromNumber, params) => {
    try {
      console.log('calling', { to, fromNumber });
      setConnParams({ from: fromNumber, to });
      setState(States.DIALING);
      let call = await device.connect({
        params: {
          To: to,
          FromNumber: fromNumber,
          ...params,
        },
      });
      setConn(call);
      // "accepted" means the call has finished connecting and the state is now "open"
      call.on('accept', () => {
        console.log('Call accepted');
        setState(States.ON_CALL);
      });
      call.on('disconnect', updateUIDisconnectedOutgoingCall);
      call.on('cancel', updateUICancelOutgoingCall);
      call.on('ringing', (hasEarlyMedia) => {
        console.log('ringing', hasEarlyMedia);
      });
    } catch (err) {
      toast.error(err.message);
      setState(States.READY);
      setConnParams({});
    }
  };

  const updateUIDisconnectedOutgoingCall = (call) => {
    console.log('call disconnected');
    setState(States.READY);
    setConn(null);
    setExternalCall();
  };

  const updateUICancelOutgoingCall = (call) => {
    console.log('call cancelled');
    setState(States.READY);
    setConn(null);
    setExternalCall();
  };

  const onReject = () => {
    conn.reject();
  };

  const onAcceptCall = () => {
    conn.accept();
  };

  const onHangUp = () => {
    device.disconnectAll();
  };

  const handleIncomingCall = (call) => {
    console.log(`Incoming call from ${call.parameters.From}`);
    setConn(call);
    setState(States.INCOMING);
    dispatch(toggleDialer(true));
    call.on('cancel', handleDisconnectedIncomingCall);
    call.on('disconnect', handleDisconnectedIncomingCall);
    call.on('accept', handleAcceptIncomingCall);
    call.on('reject', handleDisconnectedIncomingCall);
  };

  function handleAcceptIncomingCall(call) {
    console.log('Incoming call accepted.');
    setConn(call);
    setState(States.ON_CALL);
  }

  function handleDisconnectedIncomingCall() {
    console.log('Incoming call ended.');
    setState(States.READY);
    setConn(null);
    setExternalCall();
  }

  useEffect(() => {
    if (isTwillioEnabled) {
      setupTwillioVoice();
    } else {
      unsetupTwillioVoice();
    }

    return () => {
      if (device != null) {
        unsetupTwillioVoice();
      }
    };
  }, [isTwillioEnabled]);

  const renderComponent = () => {
    if (States.INCOMING === state) {
      return (
        <IncomingCall
          call={conn}
          onAccept={onAcceptCall}
          onReject={onReject}
          onHangUp={onHangUp}
        />
      );
    }
    if (States.ON_CALL === state) {
      return <OnCall onHangUp={onHangUp} call={conn} />;
    }
    if (States.DIALING === state) {
      return <Calling onHangUp={onHangUp} to={connParams.to} />;
    }
    if (externalCall) {
      return (
        <ExternalCall
          call={externalCall}
          onClose={() => {
            setExternalCall();
          }}
        />
      );
    }
    if (States.READY === state) {
      return (
        <Dialer
          onCall={onCall}
          params={showDialer}
          onClose={() => {
            dispatch(toggleDialer(false));
          }}
        />
      );
    }
    return (
      <Box
        display="flex"
        height={300}
        alignItems="center"
        justifyContent="center"
      >
        {!isTwillioEnabled ? (
          <Typography textAlign="center" variant="body1">
            Browser calling is disabled.
          </Typography>
        ) : state === States.OFFLINE ? (
          <Typography variant="body1">Offline</Typography>
        ) : (
          state === States.CONNECTING && (
            <>
              <Typography variant="body1" sx={{ mr: 2 }}>
                Connecting
              </Typography>
              <Spin spinning />
            </>
          )
        )}
      </Box>
    );
  };

  return (
    <Grow in={!!showDialer} style={{ transformOrigin: '0 -50px 0' }}>
      <Paper
        elevation={0}
        sx={{
          position: 'fixed',
          top: 70,
          right: 30,
          width: 340,
          minHeight: 300,
          maxHeight: 500,
          zIndex: 1300,
          boxShadow: '0px 8px 17px -6px rgba(0, 0, 0, 0.22)',
        }}
      >
        {showDialer && <Box height="100%">{renderComponent()}</Box>}
      </Paper>
    </Grow>
  );
};

export default Widget;
